import { fabric } from 'fabric';
import { TEXT_CONSTANTS, updateOldTextboxProperties, isCustomTextboxBehaviorEnabled } from './textbox-utils';

interface TextStyle {
  [key: string]: any;
}

interface TextStyles {
  [lineIndex: string]: {
    [charIndex: string]: TextStyle;
  };
}

export const textboxControlsVisibility = {
  tr: true,
  bl: true,
  ml: true,
  mt: false,
  mr: true,
  mb: false,
  mtr: false,
  tl: true,
  br: true,
};

const oldTextboxControlsVisibility = {
  tr: false,
  bl: false,
  ml: true,
  mt: false,
  mr: true,
  mb: false,
  mtr: false,
  tl: false,
  br: false,
};

export function setupControls(): void {
  if (isCustomTextboxBehaviorEnabled()) {
    // uniform scaling for textbox
    fabric.Textbox.prototype.lockScalingFlip = true;
    fabric.Textbox.prototype.lockUniScaling = true;
    fabric.Textbox.prototype.setControlsVisibility(textboxControlsVisibility);
  } else {
    fabric.Textbox.prototype.setControlsVisibility(oldTextboxControlsVisibility);
  }
}

export function handleTextboxBehaviorOnSelection(): void {
  /**
   * Override of fabric.Canvas.prototype._fireSelectionEvents
   *
   * Original implementation:
   * @see https://github.com/fabricjs/fabric.js/blob/v5.3.0/src/canvas.class.ts#L1242
   *
   * changes:
   * - call prepareTextboxForScaling before firing selection:created event
   * - call resetTextboxToOldBehavior when flag is disabled
   */
  (fabric.Canvas.prototype as any)._fireSelectionEvents = function (oldObjects: any, e: any) {
    // used when flag is enabled
    // add temporary data to textbox object when selection is created to be used for scaling
    // update other properties to make sure all textboxes have same behavior
    const prepareTextboxForScaling = () => {
      const activeObject = this.getActiveObject();
      if (!activeObject) {
        return;
      }
      const textboxObjects = extractTextboxObjects(activeObject);
      textboxObjects.forEach((textbox) => {
        if (activeObject.type === 'activeSelection') {
          addTemporaryPropertiesForScalingTextbox(textbox);
        }
        updateOldTextboxProperties(textbox);
      });
    };

    // reset textbox to old behavior when flag is disabled
    const resetTextboxToOldBehavior = () => {
      const activeObject = this.getActiveObject();
      if (!activeObject) {
        return;
      }
      const textboxObjects = extractTextboxObjects(activeObject);
      textboxObjects.forEach((textbox) => {
        textbox.setControlsVisibility(oldTextboxControlsVisibility);
        textbox.set('padding', TEXT_CONSTANTS.DEFAULT_PADDING);
        (textbox as any).resized = true;
      });
    };

    let somethingChanged = false;
    const objects = this.getActiveObjects();
    const added: any[] = [];
    const removed: any[] = [];
    oldObjects.forEach(function (oldObject: any) {
      if (objects.indexOf(oldObject) === -1) {
        somethingChanged = true;
        oldObject.fire('deselected', {
          e: e,
          target: oldObject,
        });
        removed.push(oldObject);
      }
    });
    objects.forEach(function (object: any) {
      if (oldObjects.indexOf(object) === -1) {
        somethingChanged = true;
        if (isCustomTextboxBehaviorEnabled()) {
          prepareTextboxForScaling();
        } else {
          resetTextboxToOldBehavior();
        }
        object.fire('selected', {
          e: e,
          target: object,
        });
        added.push(object);
      }
    });
    if (oldObjects.length > 0 && objects.length > 0) {
      somethingChanged &&
        this.fire('selection:updated', {
          e: e,
          selected: added,
          deselected: removed,
        });
    } else if (objects.length > 0) {
      this.fire('selection:created', {
        e: e,
        selected: added,
      });
    } else if (oldObjects.length > 0) {
      this.fire('selection:cleared', {
        e: e,
        deselected: removed,
      });
    }
  };

  /**
   * Override of fabric.Canvas.prototype._finalizeCurrentTransform
   *
   * Original implementation:
   * @see https://github.com/fabricjs/fabric.js/blob/v5.3.0/src/mixins/canvas_events.mixin.ts#L874
   *
   * changes: call recreateActiveSelection after transform is applied
   * we recreate the active selection after each transform, so we don't have multiple transforms applied to the same active selection
   * this is needed as a part of fixing bounding box after scaling horizontal group containing textbox objects
   */
  (fabric.Canvas.prototype as any)._finalizeCurrentTransform = function (e: any) {
    // discard activeSelection and re create it to have a new transformation matrix for each transform
    const recreateActiveSelection = () => {
      if (this._target && this._target?._objects && this._target?._objects?.length > 1) {
        const { hasTextbox, hasStickyNote } = this._target._objects.reduce(
          (acc, obj) => ({
            hasTextbox: acc.hasTextbox || obj.type === 'textbox',
            hasStickyNote:
              acc.hasStickyNote ||
              (obj.type === 'textbox' && obj.customObjectType === 'sticky-note') ||
              obj?.itemType === 'StickyNote',
          }),
          { hasTextbox: false, hasStickyNote: false },
        );
        // skip if there is no textbox, or if the selection contains a stickynote
        // TODO: remove hasStickyNote condition when the new flow for stickynote is merged
        const shouldSkipRecreateActiveSelection = !hasTextbox || hasStickyNote;
        if (shouldSkipRecreateActiveSelection) {
          return;
        }
        // Temporarily remove selection
        this.discardActiveObject();
        // Recreate the active selection
        const newSelection = new fabric.ActiveSelection(this._target._objects, { canvas: this });
        this.setActiveObject(newSelection);
      }
    };

    const transform = this._currentTransform;
    const target = transform.target;
    const options = {
      e: e,
      target: target,
      transform: transform,
      action: transform.action,
    };

    if (target._scaling) {
      target._scaling = false;
    }

    target.setCoords();

    if (transform.actionPerformed || (this.stateful && target.hasStateChanged())) {
      recreateActiveSelection(); // added line to the original function
      this._fire('modified', options);
    }
  };
}

/**
 * Handle fontSize scaling in case textbox has multiple fonts.
 * This is handled in fabric by setting fontSize with default value,
 * then in styles object they set value for every char with a specific style different from the default one.
 * Styles object consists of lines, and each line has styles for each character
 */
const updateTextboxFontSizeForMultipleFonts = function (
  textbox: fabric.Textbox,
  originalStyles: any,
  scale: number,
): void {
  if (!textbox.styles || !originalStyles) {
    return;
  }
  const styles = textbox.styles as TextStyles;

  Object.keys(styles).forEach((lineIndex) => {
    const line = styles[lineIndex];
    if (!line) {
      return;
    }
    Object.keys(line).forEach((charIndex) => {
      const charStyle = line[charIndex];
      // Check if this character has a fontSize property
      if (charStyle && charStyle.fontSize !== undefined) {
        // Scale the fontSize using the original style as reference
        const newSize =
          scale * (originalStyles[lineIndex]?.[charIndex]?.fontSize ?? textbox.fontSize);
        charStyle.fontSize = newSize;
      }
    });
  });
};

const getMinAndMaxFontSize = function (
  textbox: fabric.Textbox,
  isActiveSelection: boolean,
): { minFontSize: number; maxFontSize: number } {
  const defaultFontSize = isActiveSelection ? (textbox as any)['_tempFontSize'] : textbox.fontSize;

  // Early return if no mixed sizes
  if (!(textbox as any).hasMixedSizes) {
    return { minFontSize: defaultFontSize, maxFontSize: defaultFontSize };
  }

  const styles = (
    isActiveSelection ? (textbox as any)['_tempStyles'] : textbox.styles
  ) as TextStyles;
  let minFontSize = defaultFontSize;
  let maxFontSize = defaultFontSize;

  Object.values(styles).forEach((line) => {
    Object.values(line).forEach((char) => {
      if (char.fontSize !== undefined) {
        minFontSize = Math.min(minFontSize, char.fontSize);
        maxFontSize = Math.max(maxFontSize, char.fontSize);
      }
    });
  });

  return { minFontSize, maxFontSize };
};

/**
 * Handle horizontal group scaling containing textbox objects.
 * The expected behavior is to change the textbox width and keep the scaleX value unchanged.
 * This is done by multiplying the original width (width at selection creation) by the scaleX value.
 */
export const handleHorizontalGroupScalingForTextbox = function (
  target: fabric.Object,
  isScalingApplied: boolean,
): void {
  if (isScalingApplied && target.type === 'activeSelection') {
    const activeSelection = target as fabric.ActiveSelection;
    const scaleX = activeSelection.scaleX ?? 1;
    // Filter textbox objects and apply scaling
    activeSelection
      .getObjects()
      .filter((obj) => obj.type === 'textbox')
      .forEach((textbox) => {
        textbox.width! = scaleX * (textbox as any)['_tempWidth'];
        textbox.scaleX = 1 / scaleX;
        (textbox as any).resized = true;
      });
    adjustBoundingBoxAfterScaling(activeSelection);
  }
};

/**
 * This function is used to fix the bounding box of the active selection after scaling horizontally
 * Adjust the bounding box after scaling using _calcBounds and set coords.
 * Changing bounding box will change the position of individual objects so we need to adjust them.
 * This is done by calculating the shift in the position of the object,
 * and then reverting it by subtracting the shift from the top position.
 */
const adjustBoundingBoxAfterScaling = function (activeSelection: fabric.ActiveSelection): void {
  const objectPositions = activeSelection._objects.map((obj) => ({
    obj: obj,
    top: obj.calcTransformMatrix()[5], // Get the absolute Y position from matrix
  }));

  // Update bounding box
  activeSelection._calcBounds(true);
  activeSelection.setCoords();

  // Adjust objects to counteract the transformation shift
  objectPositions.forEach(({ obj, top }) => {
    const newTop = obj.calcTransformMatrix()[5]; // New Y position
    const shiftY = newTop - top; // Calculate how much the object was shifted
    obj.top! -= shiftY; // Revert the shift
    obj.setCoords();
  });
};

/**
 * Handle corners scaling
 * Support correct scaling behavior for textbox objects (change textbox width and fontSize)
 * This is done by by multiplying the original width and fontSize by scaleX and scaleY respectively.
 */
export const handleUniformScalingForTextbox = function (
  target: fabric.Object,
  isScalingApplied: boolean,
): void {
  if (isScalingApplied && target.type === 'activeSelection') {
    groupUniformScalingHandler(target as fabric.ActiveSelection);
  } else if (isScalingApplied && target.type === 'textbox') {
    textboxUniformScalingHandler(target as fabric.Textbox);
  }
};

// corner scaling handler for group containing textbox objects
const groupUniformScalingHandler = function (activeSelection: fabric.ActiveSelection): void {
  const scaleX = activeSelection.scaleX ?? 1;
  const scaleY = activeSelection.scaleY ?? 1;
  // Filter textbox objects and apply scaling
  activeSelection
    .getObjects()
    .filter((obj) => obj.type === 'textbox')
    .forEach((textbox) => {
      textbox.width! = scaleX * (textbox as any)['_tempWidth']; // update width
      (textbox as fabric.Textbox).fontSize! = scaleY * (textbox as any)['_tempFontSize']; // update fontSize
      updateTextboxFontSizeForMultipleFonts(
        textbox as fabric.Textbox,
        (textbox as any)['_tempStyles'],
        scaleY,
      );
      (textbox as any).resized = true;
      textbox.scaleX = 1 / scaleX;
      textbox.scaleY = 1 / scaleY;
    });
};

// corner scaling handler for textbox objects
const textboxUniformScalingHandler = function (textbox: fabric.Textbox): void {
  const scaleX = textbox.scaleX ?? 1;
  const scaleY = textbox.scaleY ?? 1;

  textbox.width! = scaleX * textbox.width!; // update width
  textbox.fontSize! = scaleY * textbox.fontSize!; // update fontSize
  updateTextboxFontSizeForMultipleFonts(textbox, textbox?.styles, scaleY);
  (textbox as any).resized = true;

  // reset scale to 1
  textbox.scaleX = 1;
  textbox.scaleY = 1;
};

/**
 * Scaling should be disabled if the fontSize will be outside the limit and after applying the scaling operation.
 * This is done by checking the expected value of fontSize after scaling and comparing it with the limits.
 * In case of mixedFontSizes, we need to check the min and max fontSize to decide if scaling should be disabled.
 */
export const shouldDisableScalingDueToFontSizeLimit = function (
  target: fabric.Object,
  scaleY: number,
): boolean {
  const textboxObjects = extractTextboxObjects(target);
  if (!textboxObjects || textboxObjects.length === 0) {
    return false;
  }
  for (const textbox of textboxObjects) {
    const { minFontSize, maxFontSize } = getMinAndMaxFontSize(
      textbox,
      target.type === 'activeSelection',
    );
    const updatedMinFontSize = (scaleY ?? 1) * minFontSize;
    const updatedMaxFontSize = (scaleY ?? 1) * maxFontSize;
    if (
      updatedMinFontSize < TEXT_CONSTANTS.MIN_FONT_SIZE ||
      updatedMaxFontSize > TEXT_CONSTANTS.MAX_FONT_SIZE
    ) {
      return true;
    }
  }
  return false;
};

const extractTextboxObjects = function (target: fabric.Object): fabric.Textbox[] {
  let textboxObjects: fabric.Textbox[] = [];
  if (target.type === 'activeSelection') {
    textboxObjects = (target as fabric.ActiveSelection)
      .getObjects()
      .filter((obj) => obj.type === 'textbox') as fabric.Textbox[];
  } else if (target.type === 'textbox') {
    textboxObjects = [target as fabric.Textbox];
  }
  return textboxObjects;
};

const addTemporaryPropertiesForScalingTextbox = function (textbox: fabric.Textbox): void {
  (textbox as any)['_tempWidth'] = textbox.width;
  (textbox as any)['_tempFontSize'] = textbox.fontSize;
  (textbox as any)['_tempStyles'] = JSON.parse(JSON.stringify(textbox.styles));
};

/**
 * This is the same function used by fabric to scale objects (scaleObject) the only difference is that here
 * we just get the scale value and don't apply it to the object.
 */
export const getScale = function (
  eventData: any,
  originalTransform: any,
  x: number,
  y: number,
  options: any,
) {
  const opposite = {
    top: 'bottom',
    bottom: 'top',
    left: 'right',
    right: 'left',
    center: 'center',
  };
  options = options || {};
  const transform = fabric.util.object.clone(originalTransform);
  const target = transform.target;
  const lockScalingX = target.lockScalingX;
  const lockScalingY = target.lockScalingY;
  const by = options.by;
  let newPoint;
  let scaleX;
  let scaleY;
  let dim;
  const scaleProportionally = scaleIsProportional(eventData, target);
  let signX;
  let signY;
  const gestureScale = transform.gestureScale;

  if (gestureScale) {
    scaleX = transform.scaleX * gestureScale;
    scaleY = transform.scaleY * gestureScale;
  } else {
    newPoint = (fabric as any).controlsUtils.getLocalPoint(
      transform,
      transform.originX,
      transform.originY,
      x,
      y,
    );
    // use of sign: We use sign to detect change of direction of an action. sign usually change when
    // we cross the origin point with the mouse. So a scale flip for example. There is an issue when scaling
    // by center and scaling using one middle control ( default: mr, mt, ml, mb), the mouse movement can easily
    // cross many time the origin point and flip the object. so we need a way to filter out the noise.
    // This ternary here should be ok to filter out X scaling when we want Y only and vice versa.
    signX = by !== 'y' ? Math.sign(newPoint.x) : 1;
    signY = by !== 'x' ? Math.sign(newPoint.y) : 1;
    if (!transform.signX) {
      transform.signX = signX;
    }
    if (!transform.signY) {
      transform.signY = signY;
    }

    if (target.lockScalingFlip && (transform.signX !== signX || transform.signY !== signY)) {
      return false;
    }

    dim = target._getTransformedDimensions();
    // missing detection of flip and logic to switch the origin
    if (scaleProportionally && !by) {
      // uniform scaling
      const distance = Math.abs(newPoint.x) + Math.abs(newPoint.y);
      const original = transform.original;
      const originalDistance =
        Math.abs((dim.x * original.scaleX) / target.scaleX) +
        Math.abs((dim.y * original.scaleY) / target.scaleY);
      const scale = distance / originalDistance;
      scaleX = original.scaleX * scale;
      scaleY = original.scaleY * scale;
    } else {
      scaleX = Math.abs((newPoint.x * target.scaleX) / dim.x);
      scaleY = Math.abs((newPoint.y * target.scaleY) / dim.y);
    }
    // if we are scaling by center, we need to double the scale
    if (transform.originX === 'center' && transform.originY === 'center') {
      scaleX *= 2;
      scaleY *= 2;
    }
    if (transform.signX !== signX && by !== 'y') {
      transform.originX = opposite[transform.originX as keyof typeof opposite];
      scaleX *= -1;
      transform.signX = signX;
    }
    if (transform.signY !== signY && by !== 'x') {
      transform.originY = opposite[transform.originY as keyof typeof opposite];
      scaleY *= -1;
      transform.signY = signY;
    }
  }
  // minScale is taken are in the setter.
  if (!by && !lockScalingX && !lockScalingY) {
    return { scaleX: scaleX, scaleY: scaleY };
  } else {
    return { scaleX: scaleX, scaleY: scaleY };
  }
};

// function used by fabric to check if the scaling is proportional
const scaleIsProportional = function (eventData: any, fabricObject: any) {
  const canvas = fabricObject.canvas;
  const uniScaleKey = canvas.uniScaleKey;
  const uniformIsToggled = eventData[uniScaleKey];
  return (
    (canvas.uniformScaling && !uniformIsToggled) || (!canvas.uniformScaling && uniformIsToggled)
  );
};
